import React, { useEffect, useState } from 'react';

import { generateColors } from '@mantine/colors-generator';
import { Loader, MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { createRoot } from 'react-dom/client';
import {
  Navigate,
  Route,
  useLocation,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Outlet,
} from 'react-router-dom';

import '@mantine/notifications/styles.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';

import { AdminRoute } from './AdminRoute';
import './i18n';
import { GlobalProcessingOverlay } from './components/GlobalProcessingOverlay';
import { SHOW_CUSTOM_POLARS_ACCESS } from './config/featureVisibility';
import { PanelStateProvider, LayoutErrorBoundary } from './context/PanelStateContext';
import { ProcessingOverlayProvider } from './context/ProcessingOverlayContext';
import { LayoutSide } from './LayoutWithSidebar';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminToolsPage } from './pages/admin/AdminToolsPage';
import { CreateUserPage } from './pages/admin/CreateUserPage';
import { ManageBoatsPage } from './pages/admin/ManageBoatsPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { AuthUser } from './pages/AuthUser';
import { HomePage } from './pages/HomePage';
import { PaymentPage } from './pages/payments/PaymentPage';
import { RaceInsightsPage } from './pages/RaceInsightsPage';
import { RealPolarsPage } from './pages/RealPolarsPage';
import { ReportsPage } from './pages/ReportsPage';
import { BoatSettings } from './pages/settings/BoatSettings';
import { ConfigurationSettings } from './pages/settings/ConfigurationSettings';
import { DataPrivacySettings } from './pages/settings/DataPrivacySettings';
import { PaymentsSettings } from './pages/settings/PaymentsSettings';
import { ProfileSettings } from './pages/settings/ProfileSettings';
import { SecuritySettings } from './pages/settings/SecuritySettings';
import { SettingsPage } from './pages/settings/SettingsPage';
import { SharingSettings } from './pages/settings/SharingSettings';
import { EmailVerificationPage } from './pages/verification/EmailVerificationPage';
import { PhoneVerificationPage } from './pages/verification/PhoneVerificationPage';
import { fetchAppConfig } from './services/config';
import {
  applyThemePreference,
  ColorScheme,
  getCurrentThemePreference,
  ThemePreference,
  subscribeToColorScheme,
} from './theme';
import { setupTokenRefresh, setAuthToken, setupAxiosInterceptors } from './utils/auth';

import './styles/normalize.css';
import './styles/index.css';
import './styles/SidebarCommon.css';

setupAxiosInterceptors();

const APP_CONFIG_STORAGE_KEY = 'appConfig';

const readStoredThemePreference = (): ThemePreference => {
  if (typeof window === 'undefined') {
    return 'dark';
  }

  try {
    const raw = localStorage.getItem(APP_CONFIG_STORAGE_KEY);
    if (!raw) {
      return 'dark';
    }
    const parsed = JSON.parse(raw) as { theme?: unknown };
    const { theme } = parsed;
    return theme === 'light' || theme === 'dark' || theme === 'system' ? theme : 'dark';
  } catch (error) {
    console.warn('No se pudo leer el tema almacenado, se usará oscuro por defecto', error);
    return 'dark';
  }
};

const persistThemePreference = (theme: ThemePreference) => {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(APP_CONFIG_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    localStorage.setItem(APP_CONFIG_STORAGE_KEY, JSON.stringify({ ...parsed, theme }));
  } catch (error) {
    console.warn('No se pudo guardar el tema seleccionado', error);
  }
};

const initialThemePreference = readStoredThemePreference();
const initialColorScheme = applyThemePreference(initialThemePreference);

const container = document.querySelector('#root');
if (!container) {
  throw new Error('Root container not found');
}
const root = createRoot(container);

const token = localStorage.getItem('token');
if (token) {
  setAuthToken(token);
  const cleanup = setupTokenRefresh();
  // Use pagehide to avoid deprecated unload listeners and still clean refresh timers.
  window.addEventListener('pagehide', cleanup);
}
// Componente que carga appConfig al inicio y muestra un loader mientras tanto
const ConfigLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const tokenLocal = localStorage.getItem('token');
        const appConfig = localStorage.getItem('appConfig');
        if (tokenLocal && !appConfig) {
          await fetchAppConfig();
        }
      } catch (error_) {
        console.error('Error cargando configuración:', error_);
        setError('No se pudo cargar la configuración. Por favor, inténtalo de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    })();
  }, [location.pathname]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div style={{ color: 'red', textAlign: 'center', marginTop: '2rem' }}>{error}</div>;
  }

  return children as React.ReactElement;
};

// Layout wrapper to use ConfigLoader with RouterProvider (renders nested routes via <Outlet />)
const ConfigLoaderLayout: React.FC = () => (
  <ConfigLoader>
    <Outlet />
  </ConfigLoader>
);

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/authUser" />;
};

// Define router with future flags to opt-in to v7 behaviors
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<ConfigLoaderLayout />}>
      <Route path="/" element={<AuthUser />} />
      <Route path="/authUser" element={<AuthUser />} />
      <Route path="/phone-verification" element={<PhoneVerificationPage />} />
      <Route path="/email-verification" element={<EmailVerificationPage />} />
      <Route
        element={
          <PrivateRoute>
            <SettingsPage />
          </PrivateRoute>
        }
      />
      <Route
        element={
          <PrivateRoute>
            <LayoutSide />
          </PrivateRoute>
        }
      >
        <Route path="home" element={<HomePage />} />
        <Route
          path="real-polars"
          element={SHOW_CUSTOM_POLARS_ACCESS ? <RealPolarsPage /> : <Navigate to="/home" replace />}
        />
      </Route>
      <Route
        path="analytics"
        element={
          <PrivateRoute>
            <AnalyticsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        <Route path="crear-usuario" element={<CreateUserPage />} />
        <Route path="barcos" element={<ManageBoatsPage />} />
        <Route path="herramientas" element={<AdminToolsPage />} />
      </Route>
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <SettingsPage />
          </PrivateRoute>
        }
      >
        <Route path="profile" element={<ProfileSettings />} />
        <Route path="configuration" element={<ConfigurationSettings />} />
        <Route path="boats" element={<BoatSettings />} />
        <Route path="data-privacy" element={<DataPrivacySettings />} />
        <Route path="sharing" element={<SharingSettings />} />
        <Route path="security" element={<SecuritySettings />} />
        <Route path="payments" element={<PaymentsSettings />} />
        <Route index element={<ProfileSettings />} /> {/* default tab */}
      </Route>
      <Route
        path="/race-insights"
        element={
          <LayoutErrorBoundary>
            <PrivateRoute>
              <RaceInsightsPage />
            </PrivateRoute>
          </LayoutErrorBoundary>
        }
      />
      <Route
        path="/reports"
        element={
          <PrivateRoute>
            <ReportsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/payment-page"
        element={
          <PrivateRoute>
            <PaymentPage />
          </PrivateRoute>
        }
      />
    </Route>,
  ),
  {
    future: {
      // Enable v7 relative splat path behavior at the router level
      v7_relativeSplatPath: true,
    },
  },
);

const AppRoot: React.FC = () => {
  const [colorScheme, setColorScheme] = useState<ColorScheme>(initialColorScheme);

  useEffect(() => subscribeToColorScheme((scheme) => setColorScheme(scheme)), []);
  useEffect(() => {
    const handleThemeShortcut = (event: KeyboardEvent) => {
      if (!event || typeof event.key !== 'string') {
        return;
      }
      const key = event.key.toLowerCase();
      const isMacCombo = event.metaKey && event.shiftKey && key === 'l' && !event.altKey;
      const isOtherCombo = event.ctrlKey && event.altKey && event.shiftKey && key === 'l';
      if (!isMacCombo && !isOtherCombo) {
        return;
      }
      event.preventDefault();
      const current = getCurrentThemePreference();
      const next: ThemePreference = current === 'dark' ? 'light' : 'dark';
      applyThemePreference(next);
      persistThemePreference(next);
    };
    window.addEventListener('keydown', handleThemeShortcut);
    return () => {
      window.removeEventListener('keydown', handleThemeShortcut);
    };
  }, []);

  return (
    <PanelStateProvider>
      <ProcessingOverlayProvider>
        <MantineProvider
          forceColorScheme={colorScheme}
          theme={{
            colors: { blue: generateColors('#1f5b7a') },
            primaryColor: 'blue',
          }}
        >
          <Notifications position="top-center" autoClose={2000} limit={1} />
          <GlobalProcessingOverlay />
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </MantineProvider>
      </ProcessingOverlayProvider>
    </PanelStateProvider>
  );
};

root.render(
  <React.StrictMode>
    <AppRoot />
  </React.StrictMode>,
);
